// Job Type data model utilities
//
// Each job type object:
//   {
//     id: string                       // 'replacement', 'newConstruction', etc, or generated for custom
//     name: string                     // Display name
//     isDefault: boolean               // True for an industry's shipped defaults
//     basedOn: string | null           // ID of the type this was cloned from (for custom types)
//     defaultStages: [{ id, name, category, order }]
//     defaultFolders: string[]         // Folder names
//     defaultTasks: [{ title, description, assignToRole }]
//     defaultCustomFields: [{ id, label, type, order, options? }]
//                                      // Custom fields an industry ships for this type.
//                                      // Same shape as companies/{id}/meta/extras.customFields
//                                      // entries (see components/CustomFields.js). Seeded into
//                                      // extras.customFields.jobs at company setup, so this is a
//                                      // TEMPLATE, not a live binding: which types a seeded field
//                                      // shows on is its own `jobTypeIds` (utils/customFieldScope.js).
//     defaultPricingPresetId: string | null
//     order: number
//   }
//
// INDUSTRY NOTE: no industry's job types live here. Each industry's shipped
// types are in its pack (utils/industryPacks/ — Window & Door's in
// windowDoor.js), and every pack is read through utils/industryRegistry.js:
// use buildDefaultJobTypesForIndustry() there.

import { DEFAULT_INDUSTRY_ID, buildDefaultJobTypesForIndustry } from './industryRegistry';

// TASK_ROLES — REMOVED 2026-09-16. An exported list of seven display strings
// with ZERO references anywhere in the app, and the only place "Sales Manager"
// appeared in source: a role that was named in a brief and never built. Two of
// its other entries ("Installer", and "Operations" spelled differently from
// everywhere else) were the same drift the role consolidation exists to end.
// If stage-task templates ever gain a "who does this" field, it reads
// ROLE_OPTIONS in services/userService.js — the one role list — rather than a
// parallel set of labels.

export const STAGE_CATEGORIES = ['Lead', 'Sent', 'Awarded', 'In Progress', 'Completed', 'Lost', 'Pending'];

// True if the company's jobTypes need to be seeded (empty/missing) or migrated
// (legacy string-array shape).
export function jobTypesNeedSeedOrMigration(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return true;
  return arr.some((t) => typeof t === 'string' || !t || typeof t !== 'object' || !t.id || !t.name);
}

// Migrate legacy jobTypes (array of strings or partial objects) to the new
// rich object model. Preserves user-added custom names by mapping them onto
// fresh custom-type objects.
//
// `shippedDefaults` is the industry's default job types — the set this
// guarantees exists (see the "Ensure all shipped defaults exist" step below).
// Callers that know the company's industry should pass
// buildDefaultJobTypesForIndustry(company.industry); omitting it falls back to
// the default industry (Window & Door), which is correct for every company
// predating the industry field. Getting this WRONG is not cosmetic: without
// it, opening Settings on a SaaS company would append the five window-and-door
// types to its pipeline.
export function normalizeJobTypes(arr, shippedDefaults) {
  const defaults = (Array.isArray(shippedDefaults) && shippedDefaults.length)
    ? shippedDefaults
    : buildDefaultJobTypesForIndustry(DEFAULT_INDUSTRY_ID);
  if (!Array.isArray(arr) || arr.length === 0) {
    return defaults;
  }
  const defaultsByName = new Map(defaults.map((d) => [d.name.toLowerCase(), d]));
  const normalized = [];
  const usedDefaultIds = new Set();
  arr.forEach((entry, i) => {
    if (typeof entry === 'string') {
      const name = entry.trim();
      if (!name) return;
      const matchDefault = defaultsByName.get(name.toLowerCase());
      if (matchDefault && !usedDefaultIds.has(matchDefault.id)) {
        usedDefaultIds.add(matchDefault.id);
        normalized.push({ ...matchDefault, order: normalized.length });
      } else {
        normalized.push({
          id: `custom_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 7)}`,
          name,
          isDefault: false,
          basedOn: null,
          defaultStages: [],
          defaultFolders: [],
          defaultTasks: [],
          defaultCustomFields: [],
          defaultPricingPresetId: null,
          order: normalized.length,
        });
      }
      return;
    }
    if (entry && typeof entry === 'object' && entry.id && entry.name) {
      normalized.push({
        id: entry.id,
        name: entry.name,
        isDefault: !!entry.isDefault,
        basedOn: entry.basedOn || null,
        defaultStages: Array.isArray(entry.defaultStages) ? entry.defaultStages : [],
        defaultFolders: Array.isArray(entry.defaultFolders) ? entry.defaultFolders : [],
        defaultTasks: Array.isArray(entry.defaultTasks) ? entry.defaultTasks : [],
        // Rebuilt explicitly (not spread), so anything not listed here is
        // dropped — defaultCustomFields must be carried through or an industry's
        // shipped field template is silently erased on the next normalize pass.
        defaultCustomFields: Array.isArray(entry.defaultCustomFields) ? entry.defaultCustomFields : [],
        defaultPricingPresetId: entry.defaultPricingPresetId || null,
        order: typeof entry.order === 'number' ? entry.order : normalized.length,
        visible: typeof entry.visible === 'boolean' ? entry.visible : true,
      });
    }
  });
  // Ensure the industry's shipped defaults exist — append any that weren't
  // already there.
  for (const d of defaults) {
    if (!normalized.some((n) => n.id === d.id)) {
      normalized.push({ ...d, order: normalized.length });
    }
  }
  // Sort by saved order. Defaults and custom types interleave freely —
  // the persisted order is the single source of truth for display order.
  normalized.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  // Reassign orders to be sequential.
  return normalized.map((t, i) => ({ ...t, order: i }));
}

/**
 * THE FIRST STAGE OF A JOB TYPE'S CONFIGURED PIPELINE — the one source of truth
 * for "where does a new record start".
 *
 * It lived in importPlan.js, which is why the CSV importer was the only
 * creation path that got this right: every other path guessed with a hardcoded
 * string or read a LEGACY company-level pipeline field. importPlan re-exports
 * this so the importer is untouched and there is still exactly one
 * implementation.
 *
 * Tolerates both stage shapes the app has shipped — a bare string, or
 * { id, name, category } — because normalizeJobTypes still migrates the former.
 */
export function firstStageName(jobType) {
  const first = (jobType && (jobType.defaultStages || [])[0]) || null;
  if (!first) return null;
  return typeof first === 'string' ? first : (first.name || null);
}

export function findJobType(jobTypes, idOrName) {
  if (!idOrName || !Array.isArray(jobTypes)) return null;
  return jobTypes.find((t) => t.id === idOrName) || jobTypes.find((t) => t.name === idOrName) || null;
}

// Resolve stage options for a stage selector. Returns the job type's
// defaultStages, falling back to the "other" type when the job's type id
// is missing/unresolvable. Lost-category stages are intentionally INCLUDED —
// callers that render kanban columns or pipeline visuals should filter them.
// Returns null when jobTypes hasn't loaded yet so callers can defer render.
export function getStageOptionsForJob(jobTypes, jobTypeId, jobTypeName) {
  if (!Array.isArray(jobTypes) || jobTypes.length === 0) return null;
  const t = findJobType(jobTypes, jobTypeId) || findJobType(jobTypes, jobTypeName);
  if (t?.defaultStages?.length) return t.defaultStages;
  const other = jobTypes.find((jt) => jt.id === 'other');
  return other?.defaultStages || [];
}

export function getJobTypePreview(jobType) {
  if (!jobType) return '';
  const stages = (jobType.defaultStages || []).length;
  const folders = (jobType.defaultFolders || []).length;
  const tasks = (jobType.defaultTasks || []).length;
  return `Includes ${stages} default stage${stages === 1 ? '' : 's'}, ${folders} folder${folders === 1 ? '' : 's'}, ${tasks} task${tasks === 1 ? '' : 's'}`;
}

export function newCustomJobTypeId() {
  return `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export function newStageId() {
  return `stage_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// Clone a job type's defaults to use as the starting point for a new custom type.
export function cloneJobTypeDefaults(source) {
  if (!source) {
    return {
      defaultStages: [],
      defaultFolders: [],
      defaultTasks: [],
      defaultCustomFields: [],
      defaultPricingPresetId: null,
    };
  }
  return {
    defaultStages: (source.defaultStages || []).map((s, i) => ({
      id: newStageId(),
      name: s.name,
      category: s.category,
      order: i,
      // Stage checkpoint config comes along; automationRules deliberately does
      // not (above it, and unchanged). The difference is that a checkpoint
      // setting DESCRIBES the stage the way its category does — "work sitting
      // here needs following up, and 5 days is too long" — while an automation
      // RUNS, creating tasks and sending mail. Dropping it would also be the
      // more surprising half: a stage the owner explicitly switched Follow Up
      // off on would come back switched on, because the category default would
      // take over again.
      checkpoints: s.checkpoints,
    })),
    defaultFolders: [...(source.defaultFolders || [])],
    defaultTasks: (source.defaultTasks || []).map((t) => ({
      title: t.title,
      description: t.description || '',
      assignToRole: t.assignToRole || 'Job Creator',
    })),
    // Field ids are kept as-is (unlike stage ids, which are re-minted): a custom
    // field id addresses a column in jobs/{id}.customData, so cloning a type
    // should point at the same data, not orphan it behind a new id.
    defaultCustomFields: (source.defaultCustomFields || []).map((f, i) => ({
      ...f,
      order: i,
      ...(Array.isArray(f.options) ? { options: [...f.options] } : {}),
    })),
    defaultPricingPresetId: source.defaultPricingPresetId || null,
  };
}
